import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, GitBranch, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/atoms/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { Switch } from '@/components/atoms/switch';
import { CollapsibleSectionHeader, FormAccordionCard } from '@/components/molecules';
import { formatCorePhase, stageDraftsFromPipeline } from './pipelineFormUtils';
import {
  CORE_PHASE_COLORS,
  CORE_PHASES,
  type CorePhase,
  type JobPipeline,
  type JobStageTrigger,
} from '@/types/pipeline';

const TRIGGERS_BY_PHASE: Record<CorePhase, JobStageTrigger[]> = {
  DRAFT: ['OCCURRENCE_COMPLETED'],
  SCHEDULED: ['VISIT_SCHEDULED', 'VISIT_DISPATCHED', 'OCCURRENCE_COMPLETED'],
  IN_PROGRESS: ['VISIT_STARTED'],
  ON_HOLD: ['VISIT_HELD'],
  COMPLETED: ['ALL_VISITS_COMPLETED'],
  INVOICED: ['INVOICE_ISSUED'],
  CLOSED: ['PAYMENT_RECEIVED'],
  CANCELLED: [],
  ARCHIVED: [],
};

const TRIGGER_LABELS: Record<JobStageTrigger, string> = {
  VISIT_SCHEDULED: 'A visit is scheduled',
  VISIT_DISPATCHED: 'A technician is dispatched',
  VISIT_STARTED: 'A visit starts',
  VISIT_HELD: 'A visit is held',
  ALL_VISITS_COMPLETED: 'All visits are completed',
  OCCURRENCE_COMPLETED: 'A recurring occurrence completes',
  INVOICE_ISSUED: 'An invoice is issued',
  PAYMENT_RECEIVED: 'The balance is paid',
};

const pipelineSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  isDefault: z.boolean(),
});

export type PipelineFormValues = z.infer<typeof pipelineSchema>;

export interface PipelineStageDraft {
  draftId: string;
  id?: string;
  name: string;
  corePhase: CorePhase;
  color: string;
  isTerminal: boolean;
  trigger?: JobStageTrigger | null;
  triggerQualifier?: string;
}

export interface PipelineFormContentProps {
  mode: 'create' | 'edit';
  pipeline: JobPipeline | null;
  isPending: boolean;
  onClose: () => void;
  onBack?: () => void;
  onCreate: (values: PipelineFormValues, stages: PipelineStageDraft[]) => void;
  onUpdate: (pipelineId: string, values: PipelineFormValues, stages: PipelineStageDraft[]) => void;
}

interface PipelineFormDrawerProps extends PipelineFormContentProps {
  open: boolean;
}

export function PipelineFormDrawer({ open, onClose, ...rest }: PipelineFormDrawerProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent className="w-full gap-0 p-0 sm:max-w-2xl">
        <PipelineFormContent onClose={onClose} {...rest} />
      </SheetContent>
    </Sheet>
  );
}

export function PipelineFormContent({
  mode,
  pipeline,
  isPending,
  onClose,
  onBack,
  onCreate,
  onUpdate,
}: PipelineFormContentProps) {
  const isEdit = mode === 'edit';
  const form = useForm<PipelineFormValues>({
    resolver: zodResolver(pipelineSchema),
    defaultValues: { name: '', isDefault: false },
    mode: 'onChange',
  });
  const [stages, setStages] = useState<PipelineStageDraft[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [stagesOpen, setStagesOpen] = useState(true);

  const sensors = useSensors(useSensor(PointerSensor));

  const initialStages = useMemo(() => stageDraftsFromPipeline(pipeline), [pipeline]);
  const hasStageChanges = !areStagesEqual(initialStages, stages);
  const hasChanges = !isEdit || form.formState.isDirty || hasStageChanges;
  const duplicateTrigger = useMemo(() => {
    const seen = new Set<string>();
    for (const stage of stages) {
      if (!stage.trigger) continue;
      const key = `${stage.trigger}:${stage.triggerQualifier?.trim().toUpperCase() ?? ''}`;
      if (seen.has(key)) return stage.trigger;
      seen.add(key);
    }
    return null;
  }, [stages]);

  useEffect(() => {
    if (mode === 'create') {
      form.reset({ name: '', isDefault: false });
      setStages([]);
      setDetailsOpen(true);
      setStagesOpen(true);
      return;
    }

    if (!pipeline) return;

    form.reset({
      name: pipeline.name,
      isDefault: Boolean(pipeline.isDefault),
    });
    setStages(stageDraftsFromPipeline(pipeline));
    setDetailsOpen(true);
    setStagesOpen(true);
  }, [form, mode, pipeline]);

  function addStage() {
    setStages((current) => [
      ...current,
      {
        draftId: createDraftId(),
        name: '',
        corePhase: 'SCHEDULED',
        color: CORE_PHASE_COLORS.SCHEDULED,
        isTerminal: false,
        trigger: null,
        triggerQualifier: '',
      },
    ]);
    setStagesOpen(true);
  }

  function updateStage(draftId: string, patch: Partial<PipelineStageDraft>) {
    setStages((current) =>
      current.map((stage) => {
        if (stage.draftId !== draftId) return stage;
        const next = { ...stage, ...patch };
        if (patch.corePhase && !patch.color) {
          next.color = CORE_PHASE_COLORS[patch.corePhase];
          if (next.trigger && !TRIGGERS_BY_PHASE[patch.corePhase].includes(next.trigger)) {
            next.trigger = null;
            next.triggerQualifier = '';
          }
        }
        return next;
      }),
    );
  }

  function removeStage(draftId: string) {
    setStages((current) => current.filter((stage) => stage.draftId !== draftId));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setStages((current) => {
        const oldIndex = current.findIndex((s) => s.draftId === active.id);
        const newIndex = current.findIndex((s) => s.draftId === over.id);
        return arrayMove(current, oldIndex, newIndex);
      });
    }
  }

  function handleSubmit(values: PipelineFormValues) {
    const normalizedStages = stages
      .map((stage) => ({ ...stage, name: stage.name.trim() }))
      .filter((stage) => stage.name.length > 0);
    const payload = {
      name: values.name.trim(),
      isDefault: values.isDefault,
    };

    if (isEdit && pipeline) {
      onUpdate(pipeline.id, payload, normalizedStages);
      return;
    }

    onCreate(payload, normalizedStages);
  }

  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center gap-2">
          {onBack ? (
            <Button type="button" variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          ) : null}
          <div>
            <SheetTitle>{isEdit ? 'Edit Job Pipeline' : 'Create Job Pipeline'}</SheetTitle>
            <SheetDescription>
              Configure the pipeline name, default behavior, and ordered workflow stages.
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
            {isEdit && !pipeline ? (
              <p className="text-sm text-muted-foreground">Pipeline not found.</p>
            ) : (
              <>
                <FormAccordionCard
                  header={
                    <CollapsibleSectionHeader
                      icon={GitBranch}
                      label="Details"
                      badge="required"
                      open={detailsOpen}
                      onToggle={() => setDetailsOpen((current) => !current)}
                    />
                  }
                  open={detailsOpen}
                  bodyClassName="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Install Pipeline" disabled={isPending} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isDefault"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
                        <div>
                          <FormLabel>Default Pipeline</FormLabel>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Use this pipeline when a job does not specify another workflow.
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            disabled={isPending}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </FormAccordionCard>

                <FormAccordionCard
                  header={
                    <CollapsibleSectionHeader
                      icon={GitBranch}
                      label="Stages"
                      badge="optional"
                      open={stagesOpen}
                      onToggle={() => setStagesOpen((current) => !current)}
                    />
                  }
                  open={stagesOpen}
                  bodyClassName="space-y-3"
                >
                  {stages.length === 0 ? (
                    <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                      No stages yet.
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={stages.map((s) => s.draftId)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {stages.map((stage, index) => (
                            <StageDraftRow
                              key={stage.draftId}
                              stage={stage}
                              index={index}
                              count={stages.length}
                              disabled={isPending}
                              onRemove={() => removeStage(stage.draftId)}
                              onChange={(patch) => updateStage(stage.draftId, patch)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}

                  {duplicateTrigger ? (
                    <p className="text-sm text-destructive" role="alert">
                      Only one stage can use {TRIGGER_LABELS[duplicateTrigger]} with the same
                      purpose or outcome.
                    </p>
                  ) : null}

                  <Button type="button" variant="outline" className="w-full" onClick={addStage}>
                    <Plus className="h-4 w-4" />
                    Add Stage
                  </Button>
                </FormAccordionCard>
              </>
            )}
          </div>

          <SheetFooter className="flex-row justify-end border-t px-5 py-4">
            <Button type="button" variant="outline" onClick={onBack ?? onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isPending ||
                Boolean(duplicateTrigger) ||
                (isEdit ? !pipeline || !hasChanges : !form.formState.isValid)
              }
            >
              {isPending ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
            </Button>
          </SheetFooter>
        </form>
      </Form>
    </>
  );
}

function StageDraftRow({
  stage,
  index,
  count,
  disabled,
  onRemove,
  onChange,
}: {
  stage: PipelineStageDraft;
  index: number;
  count: number;
  disabled: boolean;
  onRemove: () => void;
  onChange: (patch: Partial<PipelineStageDraft>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.draftId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border bg-background p-3">
      <div className="grid gap-3 sm:grid-cols-[auto_1fr_180px_76px_auto] sm:items-end">
        <div className="flex items-end pb-[3px]">
          <button
            type="button"
            className="cursor-grab text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            disabled={disabled}
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Stage Name</label>
          <Input
            value={stage.name}
            placeholder="Scheduled"
            disabled={disabled}
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Core Phase</label>
          <Select
            value={stage.corePhase}
            disabled={disabled}
            onValueChange={(value) => onChange({ corePhase: value as CorePhase })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CORE_PHASES.map((phase) => (
                <SelectItem key={phase} value={phase}>
                  {formatCorePhase(phase)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Color</label>
          <Input
            type="color"
            value={stage.color}
            disabled={disabled}
            className="h-10 w-full cursor-pointer p-1"
            onChange={(event) => onChange({ color: event.target.value })}
          />
        </div>
        <div className="flex items-end justify-end pb-[3px]">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={onRemove}
            aria-label="Remove stage"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">Enters when</label>
          <Select
            value={stage.trigger ?? 'MANUAL'}
            disabled={disabled}
            onValueChange={(value) =>
              onChange({
                trigger: value === 'MANUAL' ? null : (value as JobStageTrigger),
                ...(value === 'MANUAL' ? { triggerQualifier: '' } : {}),
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MANUAL">Moved manually</SelectItem>
              {TRIGGERS_BY_PHASE[stage.corePhase].map((trigger) => (
                <SelectItem key={trigger} value={trigger}>
                  {TRIGGER_LABELS[trigger]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {stage.trigger ? (
          <div>
            <label className="mb-1 block text-sm font-medium">Visit purpose / outcome</label>
            <Input
              value={stage.triggerQualifier ?? ''}
              maxLength={64}
              placeholder="Optional, e.g. SURVEY"
              disabled={disabled}
              onChange={(event) => onChange({ triggerQualifier: event.target.value })}
            />
          </div>
        ) : null}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
          <span className="truncate text-sm text-muted-foreground">
            Step {index + 1} of {count}
          </span>
        </div>
        <label className="flex items-center gap-2 text-sm">
          Terminal
          <Switch
            checked={stage.isTerminal}
            disabled={disabled}
            onCheckedChange={(checked) => onChange({ isTerminal: checked })}
          />
        </label>
      </div>
    </div>
  );
}

function areStagesEqual(left: PipelineStageDraft[], right: PipelineStageDraft[]) {
  if (left.length !== right.length) return false;
  return left.every((stage, index) => {
    const other = right[index];
    return (
      other &&
      stage.id === other.id &&
      stage.name === other.name &&
      stage.corePhase === other.corePhase &&
      stage.color === other.color &&
      stage.isTerminal === other.isTerminal &&
      stage.trigger === other.trigger &&
      stage.triggerQualifier === other.triggerQualifier
    );
  });
}

function createDraftId() {
  return `stage-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
